'use client'

import { CategoryFaq } from '@/services/dataTypes'
import React, { useState } from 'react'
import { ChevronDown, HelpCircle } from 'lucide-react'

interface FAQItem {
    id?: number | string
    question: string
    answer: string
}

interface CategoryFAQsProps {
    faqs: CategoryFaq[] | FAQItem[]
    categoryName?: string
    heading?: string
    text?: string
}

const InnerPageFAQs = ({ faqs, categoryName, heading }: CategoryFAQsProps) => {
    const [openIdx, setOpenIdx] = useState<number | null>(0)

    if (!faqs?.length) return null

    const title = heading || (categoryName ? `${categoryName} FAQs` : 'Frequently Asked Questions')

    return (
        <div
            className="w-full"
            itemScope
            itemType="https://schema.org/FAQPage"
        >
            {/* Header */}
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
                <h3 className="text-sm md:text-lg font-black tracking-[0.1em] text-slate-900 flex items-center gap-2 m-0">
                    <HelpCircle size={16} className="text-indigo-600 shrink-0" />
                    <span>{title}</span>
                </h3>
            </div>


            {/* Accordion Stack */}
            <div className="space-y-2.5">
                {faqs.map((faq: any, i) => {
                    const isOpen = openIdx === i
                    const qText = faq.question || faq.title || ''
                    const aText = faq.answer || faq.description || ''
                    return (
                        <div
                            key={faq.id || i}
                            className={`border rounded-xl overflow-hidden transition-all duration-300 ${isOpen
                                ? 'bg-indigo-50/20 border-indigo-200 shadow-xs'
                                : 'border-slate-100 bg-white hover:border-slate-200'
                                }`}
                            itemScope
                            itemProp="mainEntity"
                            itemType="https://schema.org/Question"
                        >
                            <button
                                type="button"
                                onClick={() => setOpenIdx(isOpen ? null : i)}
                                className="w-full flex items-center justify-between gap-2.5 px-3.5 py-3 text-left bg-transparent border-none cursor-pointer group select-none"
                                aria-expanded={isOpen}
                            >
                                <span
                                    className={`text-sm font-bold leading-snug transition-colors duration-200 flex-1 ${isOpen ? 'text-indigo-600' : 'text-slate-800 group-hover:text-indigo-600'
                                        }`}
                                    itemProp="name"
                                >
                                    {qText}
                                </span>

                                <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${isOpen
                                        ? 'bg-indigo-600 text-white rotate-180 shadow-xs'
                                        : 'bg-slate-100 text-slate-500 group-hover:bg-indigo-50 group-hover:text-indigo-600'
                                        }`}
                                >
                                    <ChevronDown size={13} />
                                </div>
                            </button>

                            {/* Collapsible Answer */}
                            <div
                                className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                                    }`}
                                itemScope
                                itemProp="acceptedAnswer"
                                itemType="https://schema.org/Answer"
                            >
                                <div className="overflow-hidden">
                                    <div className="px-3.5 pb-3.5 pt-2 text-sm text-slate-600 border-t border-slate-100 leading-relaxed prose prose-xs max-w-none">
                                        <div
                                            itemProp="text"
                                            dangerouslySetInnerHTML={{ __html: aText }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default InnerPageFAQs
